import { useState } from 'react';
import { STARTING_LIFE } from '@edh/shared';
import type { Column } from '@edh/shared';
import { initialsColor } from '../lib/avatar';
import { useLookups } from '../lib/store';

/**
 * Life over the course of a game, one line per player.
 *
 * Shared by the result screen and the table itself, because they are the same
 * question asked at two moments — "how did that go" and "how is this going" —
 * and two drawings of it would eventually disagree about what a crossover looks
 * like.
 *
 * Scrubbable, because the shape of the lines is the story and the numbers
 * behind them are the detail: "who was ahead on turn six" is a question you ask
 * after seeing the crossover, not before. Dragging along it reads out every
 * player's total at that turn, and letting go leaves it on the last one, which
 * is where the game is now.
 */

export function LifeTimeline({
  ids,
  series,
  columns,
}: {
  ids: string[];
  series: Map<string, number[]>;
  /** One per individual turn. The series carry one more point than this. */
  columns: Column[];
}) {
  const { pilot } = useLookups();
  const [at, setAt] = useState<number | null>(null);

  const W = 320;
  const H = 130;
  const LEFT = 4;
  const RIGHT = 4;
  const TOP = 8;
  /** Room under the plot for the turn numbers. */
  const AXIS = 18;

  /*
   * Scaled to what actually happened, not to what could have.
   *
   * It used to run zero to forty always, which meant a game where nobody
   * dropped below thirty was drawn as four flat lines pressed into the top
   * quarter — a picture of the rules rather than of the game. The shape is the
   * whole point of this chart, and the shape lives in the range the numbers
   * actually moved through.
   */
  const all = [...series.values()].flat();
  let hi = all.length ? Math.max(...all) : STARTING_LIFE;
  let lo = all.length ? Math.min(...all) : 0;

  /** A floor on the span, so a quiet game is drawn as a quiet game. */
  const MIN_SPAN = 10;
  if (hi - lo < MIN_SPAN) {
    const mid = (hi + lo) / 2;
    hi = mid + MIN_SPAN / 2;
    lo = mid - MIN_SPAN / 2;
  } else {
    const pad = (hi - lo) * 0.12;
    hi += pad;
    lo -= pad;
  }
  const span = Math.max(1, hi - lo);

  /**
   * Every ten inside the view, so the axis can be read.
   *
   * Needed now in a way it was not before: when the scale was always zero to
   * forty you could read a height off it from memory, and a scale that moves
   * with the game has to say what it is.
   */
  const rungs: number[] = [];
  for (let v = Math.ceil(lo / 10) * 10; v < hi; v += 10) if (v !== 0) rungs.push(v);

  /** The last plottable index. Index 0 is the opening total, before any turn. */
  const last = columns.length;
  const x = (i: number) => LEFT + (i / Math.max(1, last)) * (W - LEFT - RIGHT);
  const y = (life: number) => TOP + (1 - (life - lo) / span) * (H - TOP - AXIS);

  /**
   * Labelled by round, not by turn.
   *
   * The lines are drawn a turn at a time — that is the whole point of the
   * columns — but a four-player game runs to fifty of them and an axis reading
   * 6-1 6-2 6-3 6-4 7-1 is a smear. So the axis marks where each round begins,
   * which is the landmark people actually navigate by, and the exact turn is in
   * the readout under the cursor where there is room to say it.
   */
  const ticks: { at: number; label: string }[] = [];
  let seen = -1;
  for (let i = 0; i < columns.length; i++) {
    if (columns[i]!.turn === seen) continue;
    seen = columns[i]!.turn;
    ticks.push({ at: i, label: `${seen}` });
  }
  // Thinned to something readable, always keeping the first and last.
  const every = Math.max(1, Math.ceil(ticks.length / 9));
  const shown = ticks.filter((_, i) => i % every === 0 || i === ticks.length - 1);

  const cursor = at ?? last;

  function scrub(clientX: number, target: SVGSVGElement) {
    const box = target.getBoundingClientRect();
    const fraction = (clientX - box.left) / box.width;
    setAt(Math.round(Math.min(1, Math.max(0, fraction)) * last));
  }

  return (
    <div className="timeline-wrap">
      <svg
        className="timeline"
        viewBox={`0 0 ${W} ${H}`}
        role="img"
        aria-label="Life totals over the course of the game"
        onPointerDown={(e) => {
          e.currentTarget.setPointerCapture(e.pointerId);
          scrub(e.clientX, e.currentTarget);
        }}
        onPointerMove={(e) => {
          if (e.buttons > 0 || e.pointerType === 'mouse') scrub(e.clientX, e.currentTarget);
        }}
        onPointerLeave={() => setAt(null)}
      >
        {/* The scale, quietly, behind everything. */}
        {rungs.map((v) => (
          <g key={v}>
            <line x1={LEFT} y1={y(v)} x2={W - RIGHT} y2={y(v)} className="timeline-rung" />
            <text className="timeline-scale" x={LEFT + 2} y={y(v) - 3}>
              {v}
            </text>
          </g>
        ))}

        {/* Zero, where it falls, and drawn differently because it is not a
            gridline — it is the line everybody is trying not to cross. */}
        {lo <= 0 && hi >= 0 && (
          <line x1={x(0)} y1={y(0)} x2={x(last)} y2={y(0)} className="timeline-zero" />
        )}

        <line
          x1={x(cursor)}
          y1={TOP}
          x2={x(cursor)}
          y2={H - AXIS}
          className="timeline-cursor"
        />

        {ids.map((id) => (
          <polyline
            key={id}
            className="timeline-line"
            points={(series.get(id) ?? []).map((life, i) => `${x(i)},${y(life)}`).join(' ')}
            stroke={initialsColor(pilot(id)?.name ?? '?')}
          />
        ))}

        {ids.map((id) => {
          const life = series.get(id)?.[cursor];
          if (life === undefined) return null;
          return (
            <circle
              key={id}
              className="timeline-dot"
              cx={x(cursor)}
              cy={y(life)}
              r={3.5}
              fill={initialsColor(pilot(id)?.name ?? '?')}
            />
          );
        })}

        {shown.map((t) => (
          <text key={t.at} className="timeline-tick" x={x(t.at)} y={H - 4} textAnchor="middle">
            {t.label}
          </text>
        ))}
      </svg>

      {/* Doubles as the legend: the colour, the name and the number at the
          cursor, rather than a key you have to hold in your head. */}
      <div className="timeline-readout">
        {/* Where the axis says the round, this says the exact turn: "T6-2" is
            the second player's turn of round six, which is the resolution the
            lines are actually drawn at. Index zero is before the game started. */}
        <span className="timeline-turn">
          {cursor === 0 ? 'Start' : `T${columns[cursor - 1]?.label ?? cursor}`}
        </span>
        {ids.map((id) => (
          <span className="breakdown-key" key={id}>
            <span
              className="breakdown-swatch"
              style={{ background: initialsColor(pilot(id)?.name ?? '?') }}
            />
            {pilot(id)?.name}
            <span className="num">{series.get(id)?.[cursor] ?? '—'}</span>
          </span>
        ))}
      </div>
    </div>
  );
}
